import { Bundle, Project, Workspace } from "containers/ExecutionFilter/interfaces";
import { Link } from "react-router-dom";
import { sortAlphabetically } from "utils/sorting";
import { getLeadingSpaces, getTrailingSpaces } from "utils/utils";
import { ProjectQuickAccessEntry } from "./interfaces";

export const getTooltipOverlay = ({
    name,
    link,
    innerContent,
    onClick,
}: {
    name?: string;
    link?: string;
    innerContent?: React.ReactNode;
    onClick?: () => void;
}) => {
    const content = (
        <>
            <span className="space-background">{getLeadingSpaces(name)}</span>
            {name ? name.trim() : undefined}
            <span className="space-background">{getTrailingSpaces(name)}</span>
        </>
    );
    return (
        <div className="projectTreeTooltipOverlay">
            {link ? <Link to={link}>{content}</Link> : <div onClick={onClick}>{content}</div>}
            {innerContent}
        </div>
    );
};

export const quickAccessType = (type: "opened" | "pinned") => type;
export const quickAccessExecutionsType = (type: "opened" | "pinned") => `${type}Executions`;
export const quickAccessOpened = quickAccessType("opened");
export const quickAccessPinned = quickAccessType("pinned");
export const quickAccessExecutionsOpened = quickAccessExecutionsType("opened");
export const quickAccessExecutionsPinned = quickAccessExecutionsType("pinned");

export const groupRecordsInQuickAccess = (
    list: Project | Bundle | Workspace,
    type: "opened" | "pinned"
) => {
    return list[type].reduce(
        (acc, currentItem) => {
            if (currentItem.record_id_record.record_type === "EXECUTION") {
                acc[quickAccessExecutionsType(type)].push(currentItem);
            } else {
                acc[type].push(currentItem);
            }
            return acc;
        },
        {
            [quickAccessType(type)]: [] as ProjectQuickAccessEntry[],
            [quickAccessExecutionsType(type)]: [] as ProjectQuickAccessEntry[],
        }
    );
};

export const sortListByDate = (list: ProjectQuickAccessEntry[]) =>
    list.sort((a: ProjectQuickAccessEntry, b: ProjectQuickAccessEntry) =>
        Date.parse(a.created_at) < Date.parse(b.created_at) ? 1 : -1
    );
export const sortListByName = (list: ProjectQuickAccessEntry[]) =>
    list ? sortAlphabetically(list, (x: ProjectQuickAccessEntry) => x.record_id_record.name) : [];

export const getSortedList = (list: Project[] | Bundle[] | Workspace[]) => {
    return sortAlphabetically(
        [...list],
        (listItem: Project | Bundle | Workspace) => listItem.name
    ).map((listItem: Project | Bundle | Workspace) => {
        const openedData = groupRecordsInQuickAccess(listItem, "opened");
        const pinnedData = groupRecordsInQuickAccess(listItem, "pinned");
        return {
            ...listItem,
            [quickAccessOpened]: sortListByDate(openedData[quickAccessOpened]),
            [quickAccessPinned]: sortListByName(pinnedData[quickAccessPinned]),
            [quickAccessExecutionsOpened]: sortListByDate(openedData[quickAccessExecutionsOpened]),
            [quickAccessExecutionsPinned]: sortListByName(pinnedData[quickAccessExecutionsPinned]),
        };
    });
};

export const transformContainerFields = <T,>(container: T) => {
    return {
        ...container,
        [quickAccessPinned]: container["pinned_records"],
        [quickAccessOpened]: container["opened_records"],
    };
};

export const getListById = <T,>(list: T[]) => {
    return list.reduce((acc, item) => {
        acc[item["id"]] = transformContainerFields(item);
        return acc;
    }, {});
};
